import {
  Component,
  Inject,
  AfterViewInit,
  ElementRef,
  OnInit,
  OnDestroy,
  ViewChild,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';

import {
  SpacerService,
  SpacerBatchMaterialDto,
  SubmitMeasuredMaterialRequest
} from '../../../../services/spacer.service';
import { WebSerialScaleService } from '../../../../services/web-serial-scale.service';

// ── Dialog input data ─────────────────────────────────────────────────────────
export interface MeasureBatchMaterialData {
  batchId: number;
  material?: SpacerBatchMaterialDto;
  /** Measurement tolerance (grams). Passed from BatchDetailComponent. */
  tolerance: number;
  /** For blend measurement */
  isBlend?: boolean;
  calculatedWeight?: number;
  preferredContainer?: string;
  measuredBlendWeight?: number;
  measuredBlendContainer?: string;
  blendWeightComments?: string;
  blendContainerComments?: string;
  replacePreferredBlendContainer?: boolean;
}

type MeasureStep =
  | 'scan-barcode'    // waiting for the user to scan / type a barcode
  | 'measure-weight'  // reading the scale
  | 'review'          // barcode + weight captured
  | 'saving';         // HTTP call in progress

@Component({
  selector: 'app-measure-dry-material',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
  ],
  templateUrl: './measure-dry-material.component.html',
  styleUrls: ['./measure-dry-material.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class MeasureBatchMaterialComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly scannerInterKeyTimeoutMs = 500;
  private readonly scannerMaximumAverageInterKeyMs = 80;
  private readonly scannerMinimumLength = 4;

  @ViewChild('capturedInput') capturedInput?: ElementRef<HTMLInputElement>;
  @ViewChild('scannerInput') scannerInput?: ElementRef<HTMLInputElement>;

  // ── State ──────────────────────────────────────────────────────────────────
  step: MeasureStep = 'scan-barcode';

  capturedContainer: string = '';
  measuredWeight: number | null = null;
  replacePreferredContainer = false;
  containerComments: string = '';
  weightComments: string = '';

  scaleError: string | null = null;
  scaleMessage: string | null = null;
  submitError: string | null = null;
  isReadingScale = false;
  enterManually = false;
  private scaleAbortController: AbortController | null = null;
  private destroyed = false;

  // Scanner-as-keyboard fallback when the barcode input does not have focus.
  private scanBuffer = '';
  private scanBufferResetHandle: ReturnType<typeof setTimeout> | null = null;
  private scannerFirstCharacterAt: number | null = null;
  private scannerLastCharacterAt: number | null = null;

  // ── Getters ────────────────────────────────────────────────────────────────
  get material(): SpacerBatchMaterialDto | undefined {
    return this.data.material;
  }

  get isBlend(): boolean {
    return this.data.isBlend === true;
  }

  get materialName(): string {
    return this.isBlend ? 'Blend' : (this.material?.materialName ?? '');
  }

  get labWeight(): number {
    return this.isBlend ? (this.data.calculatedWeight ?? 0) : (this.material?.labWeight ?? 0);
  }

  get preferredContainer(): string {
    return this.isBlend ? (this.data.preferredContainer ?? '') : (this.material?.preferredContainer ?? '');
  }

  get isContainerAcceptable(): boolean {
    return this._checkContainer(this.capturedContainer);
  }

  get isWeightAcceptable(): boolean {
    return this._checkWeight(this.measuredWeight);
  }

  get scaleSupported(): boolean {
    return this.scaleService.isSupported;
  }

  get isCapturingBarcode(): boolean {
    return this.step === 'scan-barcode';
  }

  get canEnterManually(): boolean {
    return this.isCapturingBarcode
      && !this.isReadingScale
      && this.step !== 'saving'
      && (!this.enterManually || !!this.capturedContainer?.trim());
  }

  get isValid(): boolean {
    const hasBasicData = !!this.capturedContainer?.trim() && this.measuredWeight !== null;
    if (!hasBasicData) {
      return false;
    }

    if (!this.isContainerAcceptable && !this.containerComments?.trim()) {
      return false;
    }

    if (!this.isWeightAcceptable && !this.weightComments?.trim()) {
      return false;
    }

    return true;
  }

  get canSubmit(): boolean {
    return this.isValid;
  }

  constructor(
    public dialogRef: MatDialogRef<MeasureBatchMaterialComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MeasureBatchMaterialData,
    private spacerService: SpacerService,
    private scaleService: WebSerialScaleService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    // Pre-populate with any previously captured values
    if (this.isBlend) {
      this.capturedContainer = this.data.measuredBlendContainer ?? '';
      this.measuredWeight = this.data.measuredBlendWeight ?? null;
      this.containerComments = this.data.blendContainerComments ?? '';
      this.weightComments = this.data.blendWeightComments ?? '';
      this.replacePreferredContainer = this.data.replacePreferredBlendContainer ?? false;
    } else if (this.material) {
      this.capturedContainer = this.material.measuredContainer ?? '';
      this.measuredWeight = this.material.measuredWeight ?? null;
      this.containerComments = this.material.containerComments ?? '';
      this.weightComments = this.material.weightComments ?? '';
    }
  }

  ngAfterViewInit(): void {
    this.focusScannerTarget();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.scaleAbortController?.abort();
    this.clearScannerBuffer();
  }

  // ── User actions ───────────────────────────────────────────────────────────

  /** Called when the user finishes typing a barcode manually and presses Enter/Tab. */
  onBarcodeEntered(): void {
    if (!this.isCapturingBarcode || this.isReadingScale || this.step === 'saving') {
      return;
    }
    const barcode = this.normalizeBarcode(this.capturedContainer);
    if (!barcode) return;
    this.acceptCapturedBarcode(barcode);
  }

  onBarcodeModelChanged(): void {
    this.capturedContainer = this.normalizeBarcode(this.capturedContainer);
    if (this.enterManually) {
      this.scanBuffer = this.capturedContainer;
    }
  }

  onEnterManually(): void {
    if (!this.canEnterManually) {
      return;
    }

    if (!this.enterManually) {
      this.clearScannerBuffer();
      this.enterManually = true;
      this.cdr.markForCheck();
      this.focusScannerTarget();
      return;
    }

    this.onBarcodeEntered();
  }

  async onReadScale(): Promise<void> {
    await this.readMeasuredWeight();
  }

  retryScaleRead(): void {
    if (!this.capturedContainer?.trim() || this.isReadingScale || this.step !== 'measure-weight') {
      return;
    }
    void this.readMeasuredWeight();
  }

  /** Go back to the barcode scan step. */
  onRescan(): void {
    this.scaleAbortController?.abort();
    this.capturedContainer = '';
    this.scanBuffer = '';
    this.clearScannerBuffer();
    this.measuredWeight = null;
    this.scaleError = null;
    this.scaleMessage = null;
    this.enterManually = false;
    this.step = 'scan-barcode';
    this.cdr.markForCheck();
    this.focusScannerTarget();
  }

  /** Save measurement to the API and close the dialog. */
  onSubmit(): void {
    this.step = 'saving';
    this.submitError = null;
    this.cdr.markForCheck();

    if (this.isBlend) {
      // For blend, return the data to be saved by parent
      const result = {
        measuredBlendWeight: this.measuredWeight,
        measuredBlendContainer: this.capturedContainer || null,
        blendWeightComments: this.weightComments || null,
        blendContainerComments: this.containerComments || null,
        replacePreferredBlendContainer: this.replacePreferredContainer
      };
      this.dialogRef.close(result);
      return;
    }

    // For regular material
    if (!this.material) {
      this.submitError = 'Material data is missing.';
      this.step = 'review';
      this.cdr.markForCheck();
      return;
    }

    const updated: SpacerBatchMaterialDto = {
      ...this.material!,
      measuredWeight: this.measuredWeight!,
      measuredContainer: this.capturedContainer,
      weightComments: this.weightComments,
      containerComments: this.containerComments,
      replacePreferredContainer: this.replacePreferredContainer
    };
    this.dialogRef.close(updated);
  }

  onCancel(): void {
    this.scaleAbortController?.abort();
    this.dialogRef.close(null);
  }

  private async readMeasuredWeight(): Promise<void> {
    if (this.isReadingScale || !this.capturedContainer?.trim()) {
      return;
    }

    if (!this.scaleSupported) {
      this.scaleError = 'Scale reading requires Google Chrome or Microsoft Edge.';
      this.scaleMessage = null;
      this.cdr.markForCheck();
      return;
    }

    this.scaleAbortController?.abort();
    const controller = new AbortController();
    this.scaleAbortController = controller;
    this.isReadingScale = true;
    this.submitError = null;
    this.scaleError = null;
    this.scaleMessage = 'Please weight material on the connected scale.';
    this.cdr.markForCheck();

    try {
      const result = await this.scaleService.readWeightFromGrantedPort(controller.signal);
      if (this.destroyed || controller.signal.aborted || this.scaleAbortController !== controller) {
        return;
      }

      if (!result.success) {
        this.scaleError = result.errorMessage ?? 'Scale did not return a valid reading.';
        this.scaleMessage = null;
        return;
      }

      this.measuredWeight = result.weight;
      this.scaleMessage = `Measured stable weight loaded (${result.unit}).`;
      this.step = 'review';
    } catch (err: any) {
      this.scaleError = err?.message ?? 'Failed to read weight from scale.';
      this.scaleMessage = null;
    } finally {
      if (this.scaleAbortController === controller) {
        this.isReadingScale = false;
        this.scaleAbortController = null;
        this.cdr.markForCheck();
      }
    }
  }

  // ── Validation helpers (mirrors MeasuredMaterialsHelper.cs) ───────────────

  private _checkContainer(captured: string): boolean {
    const preferred = this.preferredContainer;
    if (!preferred || !captured) return true;
    const list = preferred.split(',').map(s => s.trim());
    return list.includes(captured.trim());
  }

  private _checkWeight(measured: number | null): boolean {
    if (measured === null) return true;
    const lab = this.labWeight;
    return Math.abs(measured - lab) <= this.data.tolerance;
  }

  private acceptCapturedBarcode(barcode: string): void {
    this.capturedContainer = barcode.trim();
    this.enterManually = false;
    this.clearScannerBuffer();
    this.step = 'measure-weight';
    this.scaleError = null;
    this.scaleMessage = null;
    this.cdr.markForCheck();
    void this.readMeasuredWeight();
  }

  onScannerTargetKeydown(event: KeyboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually) return;

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.commitScannerBuffer();
      return;
    }

    if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
      event.preventDefault();
      this.appendScannerCharacter(event.key);
    }
  }

  onScannerTargetInput(event: Event): void {
    if (!this.isCapturingBarcode || this.enterManually) return;

    const input = event.target as HTMLInputElement;
    const value = input.value.trim();
    input.value = '';
    if (value) {
      this.scanBuffer = value;
      this.scannerFirstCharacterAt = Date.now();
      this.scannerLastCharacterAt = this.scannerFirstCharacterAt;
      this.restartScannerBufferReset(true, true);
    }
  }

  onScannerTargetPaste(event: ClipboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually) return;

    const pastedBarcode = event.clipboardData?.getData('text')?.trim();
    if (pastedBarcode) {
      event.preventDefault();
      this.acceptCapturedBarcode(pastedBarcode);
    }
  }

  private focusScannerTarget(): void {
    setTimeout(() => {
      if (this.enterManually) {
        this.capturedInput?.nativeElement.focus();
        this.capturedInput?.nativeElement.select();
      } else if (this.isCapturingBarcode) {
        this.scannerInput?.nativeElement.focus();
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isCapturingBarcode || this.enterManually || this.step === 'saving') return;

    if (event.target === this.scannerInput?.nativeElement) return;

    if (this.isInteractiveNonBarcodeTarget(event.target)) return;

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.commitScannerBuffer();
      return;
    }

    if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) return;

    event.preventDefault();
    this.appendScannerCharacter(event.key);
  }

  private normalizeBarcode(value: string): string {
    return (value ?? '').replace(/[\r\n\t]/g, '').trim();
  }

  private isInteractiveNonBarcodeTarget(target: EventTarget | null): boolean {
    const el = target as HTMLElement | null;
    if (!el) return false;

    const scannerEl = this.scannerInput?.nativeElement;
    if (scannerEl && (el === scannerEl || scannerEl.contains(el))) return false;

    const capturedEl = this.capturedInput?.nativeElement;
    if (capturedEl && (el === capturedEl || capturedEl.contains(el))) return false;

    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el.closest('[contenteditable="true"]');
  }

  private appendScannerCharacter(character: string): void {
    const now = Date.now();
    if (this.scannerLastCharacterAt != null && now - this.scannerLastCharacterAt > this.scannerInterKeyTimeoutMs) {
      this.clearScannerBuffer();
    }
    if (this.scannerFirstCharacterAt == null) {
      this.scannerFirstCharacterAt = now;
    }
    this.scannerLastCharacterAt = now;
    this.scanBuffer += character;
    this.restartScannerBufferReset(true);
  }

  private commitScannerBuffer(allowWholeValue = false): void {
    const scannedBarcode = this.scanBuffer.trim();
    const isScannerBurst = allowWholeValue || this.isLikelyScannerBurst();
    this.clearScannerBuffer();
    if (scannedBarcode && isScannerBurst) {
      this.acceptCapturedBarcode(scannedBarcode);
    }
  }

  private isLikelyScannerBurst(): boolean {
    if (this.scanBuffer.length < this.scannerMinimumLength
      || this.scannerFirstCharacterAt == null
      || this.scannerLastCharacterAt == null) {
      return false;
    }
    const intervals = Math.max(1, this.scanBuffer.length - 1);
    return (this.scannerLastCharacterAt - this.scannerFirstCharacterAt) / intervals
      <= this.scannerMaximumAverageInterKeyMs;
  }

  private restartScannerBufferReset(commitAfterTimeout = false, allowWholeValue = false): void {
    if (this.scanBufferResetHandle) {
      clearTimeout(this.scanBufferResetHandle);
    }
    this.scanBufferResetHandle = setTimeout(() => {
      this.scanBufferResetHandle = null;
      if (commitAfterTimeout) {
        this.commitScannerBuffer(allowWholeValue);
        return;
      }
      this.clearScannerBuffer();
    }, this.scannerInterKeyTimeoutMs);
  }

  private clearScannerBuffer(): void {
    if (this.scanBufferResetHandle !== null) {
      clearTimeout(this.scanBufferResetHandle);
      this.scanBufferResetHandle = null;
    }
    this.scanBuffer = '';
    this.scannerFirstCharacterAt = null;
    this.scannerLastCharacterAt = null;
  }
}